import { getCurrentSystemMessage, getCurrentTools, type SystemMessage } from "@bastani/pi-ai";
import type { Api, Model } from "@bastani/pi-ai/compat";
import type { AgentMessage, ThinkingLevel } from "@earendil-works/pi-agent-core";
import type { AgentSessionInternalSurface as AgentSession } from "./agent-session-methods.ts";
import { getCallableTools } from "./agent-session-nested-tools.js";
import type { ToolDefinition, ToolInfo } from "./extensions/index.js";
import { applyToolModifiers } from "./settings-merge.js";
import { getSkillCatalog } from "./skill-catalog.ts";
import {
	buildSystemPrompt,
	buildSystemPromptSections,
	diffSystemPromptSections,
	type NormalizedBuildSystemPromptOptions,
	normalizeBuildSystemPromptOptions,
} from "./system-prompt.ts";
import { applyToolLoadout, isToolDeclarationHidden } from "./tool-loadout.js";
import { activatesOnRegistration, selectedToolNames } from "./tool-selection.ts";
import {
	buildToolStatusReport,
	describeToolSelection,
	type ToolSelectionSource,
	type ToolStatusReport,
} from "./tool-status.js";

export function getActiveToolNames(this: AgentSession): string[] {
	return this.agent.state.tools.map((t) => t.name);
}

export function getCallableToolNames(this: AgentSession): string[] {
	return getCallableTools(this).map((tool) => tool.name);
}

/**
 * Get all configured tools with name, description, parameter schema, and source metadata.
 */

export function getAllTools(this: AgentSession): ToolInfo[] {
	return Array.from(this._toolDefinitions.values()).map(({ definition, sourceInfo }) => ({
		name: definition.name,
		description: definition.description,
		parameters: definition.parameters,
		...(Object.hasOwn(definition, "constrainedSampling")
			? { constrainedSampling: definition.constrainedSampling }
			: {}),
		promptGuidelines: definition.promptGuidelines,
		exposure: definition.exposure ?? "direct",
		namespace: definition.namespace,
		annotations: definition.annotations,
		sourceInfo,
	}));
}

export function getToolDefinition(this: AgentSession, name: string): ToolDefinition | undefined {
	return this._toolDefinitions.get(name)?.definition;
}

function toolSelectionSource(session: AgentSession): ToolSelectionSource {
	if (session._usesDefaultTools) {
		return session.settingsManager.getDefaultTools() === undefined ? "built-in-defaults" : "default-tools-setting";
	}
	if (session._allowedToolNames) return session._allowedToolNames.size === 0 ? "no-tools" : "tools-allowlist";
	return (session._initialActiveToolNames?.length ?? 0) > 0 ? "explicit-selection" : "no-built-in-tools";
}

/**
 * Explain every registered tool (active or not, and why), where the tool selection came from,
 * and which selected names nothing registered. Format it with `formatToolStatus()`.
 */
export function getToolStatus(this: AgentSession): ToolStatusReport {
	const source = toolSelectionSource(this);
	const modifiers = [...this._defaultToolModifiers];
	return buildToolStatusReport({
		tools: [...this._toolDefinitions.values()],
		activeToolNames: this.getActiveToolNames(),
		selection: {
			source,
			description: describeToolSelection(source, modifiers),
			names: [...selectedToolNames(this)],
			modifiers,
		},
		isExcluded: this._excludedTools,
		isAllowed: this._allowedTools,
	});
}

/**
 * Set active tools by name.
 * Only tools in the registry can be enabled. Unknown tool names are ignored.
 * Also rebuilds the system prompt to reflect the new tool set.
 * Changes take effect on the next agent turn.
 */

export function setActiveToolsByName(this: AgentSession, toolNames: string[]): void {
	const previous = this.getActiveToolNames();
	this._setActiveTools(toolNames);
	const active = new Set(this.getActiveToolNames());
	if (previous.some((name) => !active.has(name))) this._pendingToolNames.clear();
}

export function _setActiveTools(this: AgentSession, toolNames: string[]): void {
	const tools = applyToolLoadout(this, toolNames);
	for (const tool of tools) this._pendingToolNames.delete(tool.name);
	this._rebuildSystemPrompt(tools.map((tool) => tool.name));
}

/** Whether compaction or branch summarization is currently running */

export function setScopedModels(
	this: AgentSession,
	scopedModels: Array<{ model: Model<Api>; thinkingLevel?: ThinkingLevel }>,
): void {
	this._scopedModels = scopedModels;
}

/** File-based prompt templates */

export function _normalizePromptSnippet(this: AgentSession, text: string | undefined): string | undefined {
	if (!text) return undefined;
	const oneLine = text
		.replace(/[\r\n]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	return oneLine.length > 0 ? oneLine : undefined;
}

export function _normalizePromptGuidelines(this: AgentSession, guidelines: string[] | undefined): string[] {
	if (!guidelines || guidelines.length === 0) {
		return [];
	}

	const unique = new Set<string>();
	for (const guideline of guidelines) {
		const normalized = guideline.trim();
		if (normalized.length > 0) {
			unique.add(normalized);
		}
	}
	return Array.from(unique);
}

export function _rebuildSystemPrompt(this: AgentSession, toolNames: string[]): void {
	const validToolNames = toolNames.filter((name) => this._toolRegistry.has(name));
	const toolSnippets: Record<string, string> = {};
	for (const name of this._toolRegistry.keys()) {
		const snippet = this._toolPromptSnippets.get(name);
		if (snippet) {
			toolSnippets[name] = snippet;
		}
	}

	const loaderSystemPrompt = this._resourceLoader.getSystemPrompt();
	const loaderAppendSystemPrompt = this._resourceLoader.getAppendSystemPrompt();
	const appendSystemPrompt = loaderAppendSystemPrompt.length > 0 ? loaderAppendSystemPrompt.join("\n\n") : undefined;
	const loadedSkills = getSkillCatalog(this._resourceLoader).modelSkills();
	const loadedContextFiles = this._resourceLoader.getAgentsFiles().agentsFiles;

	this._baseSystemPromptOptions = normalizeBuildSystemPromptOptions({
		cwd: this._cwd,
		selectedModel: this.model,
		selectedThinkingLevel: this.thinkingLevel,
		skills: loadedSkills,
		contextFiles: loadedContextFiles,
		customPrompt: loaderSystemPrompt,
		appendSystemPrompt,
		selectedTools: validToolNames,
		hiddenTools: validToolNames.filter((name) => isToolDeclarationHidden(this, name)),
		excludedTools: this._excludedToolNames ? Array.from(this._excludedToolNames) : undefined,
		toolSnippets,
		toolGuidelines: Object.fromEntries(this._toolPromptGuidelines),
	});
	if (this._systemPromptTransform) {
		this._baseSystemPromptOptions.forceSystemPrompt = this._systemPromptTransform(
			buildSystemPrompt(this._baseSystemPromptOptions),
		);
	}
}

export function _refreshBaseSystemPromptFromActiveTools(this: AgentSession): void {
	this._rebuildSystemPrompt(this.getActiveToolNames());
}

/** Diff structured prompt state; executable tool changes are declared by the agent loop. */
export function _preparePromptAndToolLoadout(
	this: AgentSession,
	options: NormalizedBuildSystemPromptOptions,
	messages: AgentMessage[] = this.agent.state.messages,
): SystemMessage | undefined {
	const current = getCurrentSystemMessage(messages);
	const selected = [...new Set(options.selectedTools)].filter((name) => this._toolRegistry.has(name));
	const recorded = (current?.toolsAdded ?? []).map((tool) => tool.name).filter((name) => selected.includes(name));
	options.selectedTools = [...recorded, ...selected.filter((name) => !recorded.includes(name))];
	options.selectedTools = applyToolLoadout(this, options.selectedTools).map((tool) => tool.name);
	options.hiddenTools = options.selectedTools.filter((name) => isToolDeclarationHidden(this, name));
	const sections = diffSystemPromptSections(current?.sections ?? {}, buildSystemPromptSections(options));
	return sections ? { role: "system", content: "", sections, timestamp: Date.now() } : undefined;
}

export function preservesInitialToolLoadout(session: AgentSession): boolean {
	return (
		(!session._usesDefaultTools && session._initialActiveToolNames !== undefined) ||
		session._orchestrationContext?.kind === "workflow-stage" ||
		(session._subagentPolicy?.depth ?? 0) >= 1
	);
}

/** The tools the selected transcript records, and every tool it ever added or removed. */
export interface RecordedToolLoadout {
	readonly current: readonly string[];
	readonly mentioned: ReadonlySet<string>;
}

/** The recorded loadout of the selected branch, or undefined when it recorded none. */
export function recordedToolLoadout(session: AgentSession): RecordedToolLoadout | undefined {
	// All system messages on the branch, including those before a compaction, whose snapshot lists only
	// the tools still added. Replaying them gives the current loadout and every tool the branch mentioned.
	const systemMessages = session.sessionManager
		.getBranch()
		.flatMap((entry) =>
			entry.type === "message" && entry.message.role === "system"
				? [entry.message]
				: entry.type === "compaction" && entry.systemMessage
					? [entry.systemMessage]
					: [],
		);
	if (systemMessages.length === 0) return undefined;
	return {
		current: getCurrentTools(systemMessages).map((tool) => tool.name),
		mentioned: new Set(
			systemMessages
				.flatMap((message) => [...(message.toolsAdded ?? []), ...(message.toolsRemoved ?? [])])
				.map((tool) => tool.name),
		),
	};
}

/**
 * Whether restoring a transcript activates a registered tool it never mentioned. Such an extension tool
 * was registered after the branch recorded its loadout, so it activates like a newly registered tool.
 * Built-in tools follow `defaultTools` instead, and opt-in tools stay inactive until a selection names them.
 */
export function activatesAfterRecordedLoadout(
	session: AgentSession,
	recorded: RecordedToolLoadout,
	name: string,
	definition: { exposure?: string; defaultActive?: boolean } | undefined,
): boolean {
	return (
		!recorded.mentioned.has(name) &&
		!session._baseToolDefinitions.has(name) &&
		activatesOnRegistration(session, name, definition, false)
	);
}

/**
 * Restore registered tools from the selected transcript, not another branch's live state.
 *
 * Explicit tool selections keep their built-in loadout while restoring dynamically registered tools.
 * Default selections restore the recorded loadout with any CLI modifiers applied. Extension tools the
 * branch never added or removed were registered after it recorded its loadout, so they are activated
 * like newly registered tools.
 */
export function _restoreToolsFromTranscript(this: AgentSession, options?: { startup?: boolean }): void {
	this._pendingToolNames.clear();
	const recorded = recordedToolLoadout(this);
	// No recorded loadout: keep the current tools.
	if (!recorded) return;
	const keepLoadout = options?.startup && preservesInitialToolLoadout(this);
	const modifiers = options?.startup && this._usesDefaultTools ? this._defaultToolModifiers : [];
	// Removed tools are mentioned too, so they stay inactive.
	const newTools = [...this._toolRegistry.keys()].filter((name) =>
		activatesAfterRecordedLoadout(this, recorded, name, this._toolDefinitions.get(name)?.definition),
	);
	const declared = applyToolModifiers([...recorded.current, ...newTools], modifiers);
	if (!keepLoadout) {
		this._pendingToolNames = new Set(declared);
		this._setActiveTools(declared);
		return;
	}
	const restored = declared.filter((name) => !this._baseToolDefinitions.has(name));
	this._pendingToolNames = new Set(restored);
	this._setActiveTools(applyToolModifiers([...this.getActiveToolNames(), ...restored], modifiers));
}

// =========================================================================
// Prompting
// =========================================================================

/**
 * Send a prompt to the agent.
 * - Handles extension commands (registered via pi.registerCommand) immediately, even during streaming
 * - Expands file-based prompt templates by default
 * - During streaming, queues via steer() or followUp() based on streamingBehavior option
 * - Validates model and API key before sending (when not streaming)
 * @throws Error if streaming and no streamingBehavior specified
 * @throws Error if no model selected or no API key available (when not streaming)
 */

export const agentSessionStateMethods = {
	getActiveToolNames,
	getCallableToolNames,
	getAllTools,
	getToolDefinition,
	getToolStatus,
	setActiveToolsByName,
	_setActiveTools,
	setScopedModels,
	_normalizePromptSnippet,
	_normalizePromptGuidelines,
	_rebuildSystemPrompt,
	_preparePromptAndToolLoadout,
	_restoreToolsFromTranscript,
	_refreshBaseSystemPromptFromActiveTools,
};
